import React from "react";
import { useNavigate } from "react-router-dom";
import { Zap, Target, Flame, Smartphone, Upload, ScanLine, Trophy, ArrowRight, Gauge, CheckCircle2, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { PlanCard } from "@/components/PlanCard";

const HERO_IMG = "https://images.unsplash.com/photo-1517747614396-d21a78b850e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();

  const startPredicting = () => {
    if (!user) navigate("/register");
    else if (user.role === "admin") navigate("/admin");
    else navigate("/predictor");
  };
  const selectPlan = (plan) => {
    if (!user) navigate("/register");
    else navigate(`/payment/${encodeURIComponent(plan.name)}`);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden pitch-gradient">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(11,15,23,0.75),rgba(11,15,23,0.95))" }} />
        </div>
        <div className="vw-container relative py-20 md:py-28">
          <div className="max-w-3xl vw-fade-up">
            <span className="vw-label inline-flex items-center gap-2 mb-5">
              <Sparkles size={14} /> Virtual Football Intelligence
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
              WIN INSTANT VIRTUAL <span style={{ color: "#00FF66" }}>WITH VIRTUALWIN</span>
            </h1>
            <p className="mt-5 text-xl sm:text-2xl font-semibold text-slate-200">Upload. Analyze. Predict. ⚡</p>
            <p className="mt-4 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">
              Your next virtual football prediction starts here. Upload your virtual football fixture
              screenshot and let VirtualWin quickly identify the strongest match outcomes.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={startPredicting} className="vw-btn-primary text-base inline-flex items-center justify-center gap-2" data-testid="hero-start-btn">
                START PREDICTING NOW <ArrowRight size={18} />
              </button>
              <button onClick={() => navigate("/plans")} className="vw-btn-outline text-base" data-testid="hero-plans-btn">
                VIEW PLANS
              </button>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm text-slate-400">
              {["Fast analysis", "Home / Draw / Away only", "Top 3 strongest picks"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-400" /> {t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* READY TO TRY */}
      <section className="vw-container py-16 md:py-20">
        <div className="vw-glow-card p-8 sm:p-12 text-center">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">READY TO TRY VIRTUALWIN?</h2>
          <p className="mt-3 text-slate-400 max-w-2xl mx-auto">
            Create your account, choose your plan, upload your fixture, and get your predictions in seconds.
          </p>
          <button onClick={startPredicting} className="vw-btn-primary mt-6 inline-flex items-center gap-2" data-testid="ready-start-btn">
            START PREDICTING NOW <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* WHY */}
      <section className="vw-container py-10 md:py-16">
        <div className="text-center mb-12">
          <span className="vw-label">Why VirtualWin?</span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2">Built for speed and focus</h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Zap, t: "FAST ANALYSIS", d: "Quickly process uploaded virtual football fixture screenshots." },
            { icon: Target, t: "FOCUSED PREDICTIONS", d: "Receive clear Home Win, Draw, or Away Win selections." },
            { icon: Flame, t: "TOP 3 SELECTIONS", d: "More than 3 fixtures? VirtualWin displays only the 3 strongest selections." },
            { icon: Smartphone, t: "EASY TO USE", d: "A simple experience designed for both mobile phones and computers." },
          ].map((f, i) => (
            <div key={i} className="vw-glow-card p-6" data-testid={`feature-${i}`}>
              <div className="h-11 w-11 grid place-items-center rounded-xl bg-emerald-500/10 text-emerald-400 mb-4">
                <f.icon size={22} />
              </div>
              <h3 className="font-display font-bold text-white text-base">{f.t}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="vw-container py-14 md:py-20">
        <div className="text-center mb-12">
          <span className="vw-label">How It Works</span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2">Three steps to your VirtualWin</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { n: "01", t: "UPLOAD", d: "Upload a screenshot containing your virtual football fixtures.", icon: Upload },
            { n: "02", t: "ANALYZE", d: "VirtualWin quickly analyzes the available fixtures.", icon: ScanLine },
            { n: "03", t: "PREDICT", d: "Receive the selected Home Win, Draw, or Away Win predictions.", icon: Trophy },
          ].map((s, i) => (
            <div key={i} className="vw-card p-7 relative overflow-hidden" data-testid={`step-${i}`}>
              <span className="font-display text-6xl font-black text-slate-800/80 absolute top-3 right-4">{s.n}</span>
              <div className="h-11 w-11 grid place-items-center rounded-xl bg-emerald-500/10 text-emerald-400 mb-4"><s.icon size={22} /></div>
              <h3 className="font-display font-bold text-white text-lg">{s.n} — {s.t}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <button onClick={startPredicting} className="vw-btn-primary inline-flex items-center gap-2" data-testid="how-start-btn">
            START PREDICTING NOW <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="vw-container py-14 md:py-20">
        <div className="grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <span className="vw-label">About VirtualWin</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2 leading-tight">
              YOUR VIRTUAL. YOUR CHANCE. YOUR VIRTUALWIN.
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              VirtualWin brings speed, simplicity, and virtual football analysis together in one modern platform.
            </p>
            <p className="mt-3 text-slate-400 leading-relaxed">
              Instead of navigating through complicated options, VirtualWin keeps the experience simple: upload your
              virtual fixture screenshot, analyze the available matches, and receive focused match-outcome predictions.
            </p>
            <p className="mt-3 text-slate-400 leading-relaxed">
              Whether you're checking a single fixture or uploading a screenshot containing multiple matches,
              VirtualWin is designed to make the process quick and easy.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { t: "FAST", d: "Results in seconds", icon: Zap },
              { t: "SIMPLE", d: "Upload & go", icon: Smartphone },
              { t: "FOCUSED", d: "Only match outcomes", icon: Target },
              { t: "SMART SELECTION", d: "Top 3 strongest picks", icon: Gauge },
            ].map((c, i) => (
              <div key={i} className="vw-glow-card p-6" data-testid={`about-card-${i}`}>
                <c.icon className="text-emerald-400 mb-3" size={24} />
                <h4 className="font-display font-bold text-white">{c.t}</h4>
                <p className="mt-1 text-sm text-slate-400">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="vw-container py-14 md:py-20">
        <div className="text-center mb-12">
          <span className="vw-label">Plans</span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2">Choose your VirtualWin plan</h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {(settings?.plans || []).map((p) => (
            <PlanCard key={p.name} plan={p} onSelect={selectPlan} compact />
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="vw-container pb-20">
        <div className="relative overflow-hidden rounded-3xl p-10 sm:p-16 text-center"
          style={{ background: "linear-gradient(135deg,#0E1A14,#0B0F17)", border: "1px solid rgba(0,255,102,0.2)" }}>
          <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full" style={{ background: "rgba(0,255,102,0.12)", filter: "blur(60px)" }} />
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-white relative">READY TO WIN INSTANT VIRTUAL?</h2>
          <p className="mt-4 text-slate-300 relative">Upload your fixture. Analyze the matches. Discover your VirtualWin.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center relative">
            <button onClick={startPredicting} className="vw-btn-primary text-base inline-flex items-center justify-center gap-2" data-testid="final-start-btn">
              START PREDICTING NOW <ArrowRight size={18} />
            </button>
            <button onClick={() => navigate("/plans")} className="vw-btn-outline text-base" data-testid="final-plans-btn">VIEW PLANS</button>
          </div>
        </div>
      </section>
    </div>
  );
}
